import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useContact } from "@/lib/contact";
import { scrollToSection, scrollToTop } from "@/lib/lenis";
import { api } from "@/lib/api";
import { HOTELS_SECTION_ENABLED } from "@/lib/sectionFlags";

const LINKS = [
  { id: "about", label: "نبذة عنا", testid: "nav-link-about" },
  { id: "reviews", label: "آراء العملاء", testid: "nav-link-reviews" },
  { id: "contact", label: "تواصل معنا", testid: "nav-link-contact" },
  { id: "location", label: "موقعنا", testid: "nav-link-location" },
];

const DEST_FALLBACK = [{ id: "maldives", label: "اكتشف المالديف", url: "/maldives" }];

export const Navbar = () => {
  const CONTACT = useContact();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [dests, setDests] = useState([]);
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/destinations").then(({ data }) => setDests(data.length ? data : DEST_FALLBACK)).catch(() => setDests(DEST_FALLBACK));
  }, []);

  // الشعار وعنصر «الصفحة الرئيسية»: نفس التبويب، أعلى الصفحة، إغلاق قائمة الجوال
  const goHome = (e) => {
    setOpen(false);
    setDestOpen(false);
    if (onHome) {
      e.preventDefault();
      scrollToTop(false);
    } else {
      window.setTimeout(() => scrollToTop(true), 80);
    }
  };

  // روابط الأقسام: في الرئيسية تمرير سلس مع تحديث الهاش؛ من باقي الصفحات ينتقل الرابط
  // إلى /‎#id وتتولى HomePage التمرير بعد اكتمال التحميل (تعمل أيضًا عند الفتح المباشر)
  const goSection = (e, id) => {
    setOpen(false);
    setDestOpen(false);
    if (onHome) {
      e.preventDefault();
      window.history.pushState(null, "", `/#${id}`);
      scrollToSection(`#${id}`);
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open ? "bg-[#FFFFFF]/90 backdrop-blur-xl border-b border-slate-200 shadow-lg shadow-slate-200/70" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" onClick={goHome} data-testid="navbar-brand-logo" aria-label="دليل المسافر للحجوزات — الصفحة الرئيسية">
          <Logo compact />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <Link
            to="/"
            data-testid="nav-link-home"
            onClick={goHome}
            aria-current={onHome ? "page" : undefined}
            className={`text-sm font-bold transition-colors duration-300 ${onHome ? "text-brand" : "text-slate-700 hover:text-[#C80000]"}`}
          >
            الصفحة الرئيسية
          </Link>
          {dests.length > 0 && (
            <div className="relative" onMouseEnter={() => setDestOpen(true)} onMouseLeave={() => setDestOpen(false)}>
              <button
                onClick={() => setDestOpen(!destOpen)}
                data-testid="nav-destinations-toggle"
                aria-expanded={destOpen}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:text-[#A60000] transition-colors duration-300"
              >
                الوجهات
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${destOpen ? "rotate-180" : ""}`} />
              </button>
              {destOpen && (
                <div className="absolute top-full start-0 pt-3 z-50" data-testid="nav-destinations-menu">
                  <div className="min-w-52 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-xl p-2 shadow-xl shadow-slate-200/70">
                    {dests.map((d) => (
                      <Link
                        key={d.id}
                        to={d.url}
                        data-testid={`nav-destination-${d.id}`}
                        onClick={() => setDestOpen(false)}
                        className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-[#C80000]/15 hover:text-brand transition-colors"
                      >
                        {d.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {HOTELS_SECTION_ENABLED && (
            <Link
              to="/hotels"
              data-testid="nav-link-hotels"
              className="text-sm font-bold text-slate-700 hover:text-[#C80000] transition-colors duration-300"
            >
              فنادق مقترحة
            </Link>
          )}
          <Link
            to="/offers"
            data-testid="nav-link-offers"
            className="text-sm font-bold text-brand hover:text-[#A60000] transition-colors duration-300"
          >
            عروض السفر
          </Link>
          <Link
            to="/plan-trip"
            data-testid="nav-link-plan-trip"
            className="text-sm font-bold text-brand hover:text-[#A60000] transition-colors duration-300"
          >
            رتّب رحلتك
          </Link>
          <Link
            to="/flight-quote"
            data-testid="nav-link-flight-quote"
            className="text-sm font-bold text-brand hover:text-[#A60000] transition-colors duration-300"
          >
            تسعير طيران
          </Link>
          {LINKS.map((l) => (
            <Link
              key={l.id}
              to={`/#${l.id}`}
              data-testid={l.testid}
              onClick={(e) => goSection(e, l.id)}
              className="hidden xl:inline-flex text-sm font-semibold text-slate-600 hover:text-brand transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phoneLink}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#C80000] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#E01010] transition-colors duration-300"
          >
            <Phone className="h-4 w-4" />
            <span dir="ltr">{CONTACT.phoneDisplay}</span>
          </a>
          <button
            onClick={() => setOpen(!open)}
            aria-label="القائمة"
            data-testid="nav-mobile-toggle"
            className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-900"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-[#FFFFFF]/95 backdrop-blur-xl border-t border-slate-200 px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          <Link
            to="/"
            data-testid="nav-link-home-mobile"
            onClick={goHome}
            aria-current={onHome ? "page" : undefined}
            className={`text-right text-base font-bold ${onHome ? "text-brand" : "text-slate-700"}`}
          >
            الصفحة الرئيسية
          </Link>
          {dests.length > 0 && (
            <div>
              <button
                onClick={() => setDestOpen(!destOpen)}
                data-testid="nav-destinations-toggle-mobile"
                aria-expanded={destOpen}
                className="flex w-full items-center justify-between text-base font-bold text-brand"
              >
                الوجهات
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${destOpen ? "rotate-180" : ""}`} />
              </button>
              {destOpen && (
                <div className="mt-3 flex flex-col gap-3 border-r-2 border-[#C80000]/30 pr-4" data-testid="nav-destinations-menu-mobile">
                  {dests.map((d) => (
                    <Link
                      key={d.id}
                      to={d.url}
                      data-testid={`nav-destination-mobile-${d.id}`}
                      onClick={() => { setOpen(false); setDestOpen(false); }}
                      className="text-right text-sm font-semibold text-slate-900 hover:text-brand transition-colors"
                    >
                      {d.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          {HOTELS_SECTION_ENABLED && (
            <Link
              to="/hotels"
              data-testid="nav-link-hotels-mobile"
              onClick={() => setOpen(false)}
              className="text-right text-base font-bold text-slate-700"
            >
              فنادق مقترحة
            </Link>
          )}
          <Link
            to="/offers"
            data-testid="nav-link-offers-mobile"
            onClick={() => setOpen(false)}
            className="text-right text-base font-bold text-brand"
          >
            عروض السفر
          </Link>
          <Link
            to="/plan-trip"
            data-testid="nav-link-plan-trip-mobile"
            onClick={() => setOpen(false)}
            className="text-right text-base font-bold text-brand"
          >
            رتّب رحلتك
          </Link>
          <Link
            to="/flight-quote"
            data-testid="nav-link-flight-quote-mobile"
            onClick={() => setOpen(false)}
            className="text-right text-base font-bold text-brand"
          >
            اطلب تسعير طيران
          </Link>
          {LINKS.map((l) => (
            <Link
              key={l.id}
              to={`/#${l.id}`}
              data-testid={`${l.testid}-mobile`}
              onClick={(e) => goSection(e, l.id)}
              className="text-right text-base font-semibold text-slate-900 hover:text-brand transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <a href={CONTACT.phoneLink} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C80000] px-5 py-3 text-sm font-bold text-white">
            <Phone className="h-4 w-4" />
            <span dir="ltr">{CONTACT.phoneDisplay}</span>
          </a>
        </nav>
      )}
    </header>
  );
};
